export interface CityPriceBreakdown {
  type: string;
  price: string;
  note?: string;
}

export interface CityDeepContent {
  /** Unique intro paragraph about moving in this city */
  uniqueIntro: string;
  /** Cost breakdown table rows */
  priceBreakdown: CityPriceBreakdown[];
  /** Parking/permit rules */
  parkingInfo: string;
  /** Lift/elevator specifics */
  liftInfo: string;
  /** Student/expat specific tips */
  demographicTips?: string;
  /** Seasonal advice */
  seasonalAdvice: string;
  /** Distance-based cost examples */
  distanceExamples?: { route: string; price: string }[];
  /** Local regulations or municipality tips */
  municipalityTips?: string;
}

export interface CityInfo {
  name: string;
  province: string;
  population: string;
  avgPrice: string;
  neighborhoods: string[];
  description: string;
  /** Rich content for SEO depth — optional, only for key cities */
  deepContent?: CityDeepContent;
}

export const cityData: Record<string, CityInfo> = {
  // Original 20 cities
  amsterdam: {
    name: "Amsterdam",
    province: "Noord-Holland",
    population: "920.000",
    avgPrice: "€650 - €1.800",
    neighborhoods: ["Centrum", "Zuid", "West", "Oost", "Noord", "Nieuw-West", "Zuidoost"],
    description: "Amsterdam is de grootste stad van Nederland met smalle grachtenpanden en steile trappen. Dit maakt verhuizen vaak uitdagender en vraagt om ervaren verhuizers met de juiste apparatuur zoals verhuisliften."
  },
  rotterdam: {
    name: "Rotterdam",
    province: "Zuid-Holland",
    population: "660.000",
    avgPrice: "€500 - €1.400",
    neighborhoods: ["Centrum", "Kralingen", "Hillegersberg", "Delfshaven", "Feijenoord", "IJsselmonde"],
    description: "Rotterdam is een moderne havenstad met veel nieuwbouw en hoogbouw. De brede straten en moderne infrastructuur maken verhuizen vaak makkelijker dan in andere grote steden."
  },
  "den-haag": {
    name: "Den Haag",
    province: "Zuid-Holland",
    population: "550.000",
    avgPrice: "€550 - €1.500",
    neighborhoods: ["Centrum", "Scheveningen", "Loosduinen", "Segbroek", "Escamp", "Laak"],
    description: "Den Haag combineert historische wijken met moderne nieuwbouw. Van de monumentale panden in het centrum tot de strandhuizen in Scheveningen - elke verhuizing vraagt om maatwerk."
  },
  utrecht: {
    name: "Utrecht",
    province: "Utrecht",
    population: "360.000",
    avgPrice: "€550 - €1.400",
    neighborhoods: ["Binnenstad", "Oost", "West", "Zuid", "Leidsche Rijn", "Vleuten-De Meern", "Overvecht", "Noordoost", "Zuilen", "Lombok"],
    description: "Utrecht is een compacte stad met een historisch centrum vol grachtenpanden. De smalle straten en beperkte parkeerruimte vragen om verhuizers die de stad goed kennen.",
    deepContent: {
      uniqueIntro: "Utrecht is na Amsterdam de snelst groeiende stad van Nederland. Met jaarlijks meer dan 28.000 verhuisbewegingen is het de op twee na drukste verhuisstad van het land. De unieke Utrechtse grachten met hun werfkelders, de smalle middeleeuwse straten in de binnenstad en de enorme nieuwbouwwijk Leidsche Rijn zorgen voor sterk uiteenlopende verhuisuitdagingen. Of u nu van een studentenkamer in de Binnenstad naar een gezinswoning in Vleuten-De Meern verhuist, of vanuit een ander deel van Nederland naar Utrecht trekt: een goed voorbereid plan en een verhuisbedrijf met lokale kennis zijn essentieel.",
      priceBreakdown: [
        { type: "Studentenkamer / studio", price: "€250 - €450", note: "Zelf inpakken, kleine bus" },
        { type: "Appartement (2-3 kamers)", price: "€550 - €900", note: "Verhuislift vaak nodig in binnenstad" },
        { type: "Eengezinswoning", price: "€800 - €1.400", note: "Inclusief demonteren meubels" },
        { type: "Vrijstaande woning", price: "€1.200 - €2.200", note: "Grote inboedel, meerdere verhuizers" },
        { type: "Verhuislift (per dag)", price: "€175 - €350", note: "Verplicht bij veel grachtenpanden" },
        { type: "Inpakservice (volledig)", price: "€200 - €500", note: "Afhankelijk van inboedelgrootte" },
        { type: "Tijdelijke opslag (per maand)", price: "€75 - €200", note: "Populair bij tussenhuren" },
      ],
      parkingInfo: "In het centrum van Utrecht geldt een milieuzone en zijn parkeervergunningen verplicht voor verhuiswagens groter dan 3,5 ton. U kunt een tijdelijke ontheffing aanvragen via de gemeente Utrecht (minimaal 5 werkdagen van tevoren). In wijken als Lombok, Wittevrouwen en de Binnenstad is het daarnaast verstandig om een parkeerplek te reserveren met verhuisborden. Buiten de singels, in wijken als Leidsche Rijn en Vleuten-De Meern, is parkeren doorgaans eenvoudiger.",
      liftInfo: "De Utrechtse binnenstad staat bekend om steile, smalle trappen — vooral in de historische grachtenpanden langs de Oudegracht en Nieuwegracht. Een verhuislift is hier in meer dan 60% van de gevallen noodzakelijk. In nieuwbouwwijken als Leidsche Rijn en Papendorp beschikken de meeste appartementen over een ruime lift, waardoor een verhuislift vaak niet nodig is. Tip: laat het verhuisbedrijf vooraf een inspectie doen zodat u niet voor verrassingen komt te staan.",
      demographicTips: "Utrecht heeft de jongste bevolking van alle grote Nederlandse steden: ruim 20% van de inwoners is student. Dit betekent dat rond 1 juli en 1 september de vraag naar verhuisbedrijven piekt vanwege studenten die verhuizen. Voor gezinnen die naar Leidsche Rijn of Vleuten-De Meern trekken, is het juist rustiger in de zomermaanden buiten deze pieken. Expats die via het Utrecht Science Park of de Universiteit Utrecht naar de stad komen, doen er goed aan een verhuisbedrijf te kiezen dat Engels spreekt en ervaring heeft met internationale verhuizingen.",
      seasonalAdvice: "De drukste verhuisperiodes in Utrecht zijn eind juni tot begin september (studentenverhuizingen), de laatste week van de maand en rond de feestdagen. In deze periodes betaalt u gemiddeld 15-25% meer. De voordeligste maanden zijn januari, februari en november. Verhuizen op dinsdag of woensdag is doorgaans €50-€100 goedkoper dan op vrijdag of zaterdag.",
      distanceExamples: [
        { route: "Utrecht → Amsterdam (52 km)", price: "€700 - €1.600" },
        { route: "Utrecht → Rotterdam (62 km)", price: "€750 - €1.700" },
        { route: "Utrecht → Amersfoort (23 km)", price: "€550 - €1.200" },
        { route: "Utrecht → Hilversum (22 km)", price: "€550 - €1.200" },
        { route: "Utrecht → Nieuwegein (8 km)", price: "€450 - €1.000" },
        { route: "Binnen Utrecht (lokaal)", price: "€400 - €900" },
      ],
      municipalityTips: "De gemeente Utrecht biedt een gratis verhuismelding via MijnOverheid.nl. Vergeet niet om binnen 5 dagen na uw verhuizing uw adreswijziging door te geven bij de gemeente. Voor verhuizingen naar of vanuit een sociale huurwoning via Woningnet Utrecht gelden aanvullende regels rond inschrijving en urgentie. Bekijk ook de gemeentelijke subsidies voor verduurzaming als u naar een koopwoning in Utrecht verhuist — sommige wijken komen in aanmerking voor isolatiesubsidies die u kunt combineren met uw verhuizing.",
    },
  },
  eindhoven: {
    name: "Eindhoven",
    province: "Noord-Brabant",
    population: "240.000",
    avgPrice: "€450 - €1.200",
    neighborhoods: ["Centrum", "Strijp", "Woensel", "Gestel", "Stratum", "Tongelre"],
    description: "Eindhoven is de technologiehoofdstad van Nederland met veel expats en studenten. De stad kent een mix van historische wijken en moderne nieuwbouwprojecten."
  },
  tilburg: {
    name: "Tilburg",
    province: "Noord-Brabant",
    population: "225.000",
    avgPrice: "€400 - €1.100",
    neighborhoods: ["Centrum", "Noord", "West", "Oost", "Zuid", "Reeshof"],
    description: "Tilburg is een gezellige Brabantse stad met betaalbare woningen en goede bereikbaarheid. De relatief brede straten maken verhuizen hier vaak vlot."
  },
  groningen: {
    name: "Groningen",
    province: "Groningen",
    population: "235.000",
    avgPrice: "€400 - €1.000",
    neighborhoods: ["Centrum", "Paddepoel", "Vinkhuizen", "Helpman", "Beijum", "Lewenborg"],
    description: "Groningen is de grootste stad van Noord-Nederland met een levendige studentenpopulatie. Het historische centrum vraagt om ervaren verhuizers die de smalle straten kennen."
  },
  almere: {
    name: "Almere",
    province: "Flevoland",
    population: "220.000",
    avgPrice: "€400 - €1.000",
    neighborhoods: ["Almere Stad", "Almere Haven", "Almere Buiten", "Almere Poort", "Almere Hout"],
    description: "Almere is een moderne stad met ruim opgezette wijken en veel nieuwbouw. De brede straten en goede parkeermogelijkheden maken verhuizen hier relatief eenvoudig."
  },
  breda: {
    name: "Breda",
    province: "Noord-Brabant",
    population: "185.000",
    avgPrice: "€450 - €1.200",
    neighborhoods: ["Centrum", "Princenhage", "Ginneken", "Haagse Beemden", "Teteringen"],
    description: "Breda is een historische Brabantse stad met een mix van monumentale panden en moderne nieuwbouw. De gezellige binnenstad vraagt om zorgvuldig manoeuvreren."
  },
  nijmegen: {
    name: "Nijmegen",
    province: "Gelderland",
    population: "180.000",
    avgPrice: "€450 - €1.100",
    neighborhoods: ["Centrum", "Hees", "Dukenburg", "Lindenholt", "Lent", "Hatert"],
    description: "Nijmegen is de oudste stad van Nederland met een heuvelachtig landschap. De steile straten in sommige wijken vragen om extra aandacht bij het verhuizen."
  },
  arnhem: {
    name: "Arnhem",
    province: "Gelderland",
    population: "165.000",
    avgPrice: "€400 - €1.000",
    neighborhoods: ["Centrum", "Malburgen", "Presikhaaf", "Klarendal", "Velperweg"],
    description: "Arnhem ligt op de Veluwe met veel groen en heuvelachtig terrein. De variatie in woningen van villa's tot appartementen vraagt om flexibele verhuisoplossingen."
  },
  haarlem: {
    name: "Haarlem",
    province: "Noord-Holland",
    population: "165.000",
    avgPrice: "€600 - €1.600",
    neighborhoods: ["Centrum", "Noord", "Schalkwijk", "Heemstede"],
    description: "Haarlem is een monumentale stad vlakbij Amsterdam met veel historische panden. De nauwe straatjes en grachtenpanden maken een verhuislift vaak onmisbaar."
  },
  enschede: {
    name: "Enschede",
    province: "Overijssel",
    population: "160.000",
    avgPrice: "€350 - €900",
    neighborhoods: ["Centrum", "Roombeek", "Glanerbrug", "Lonneker", "Stadsveld"],
    description: "Enschede is een levendige Twentse stad met betaalbare woningen. De goede infrastructuur en ruime straten maken verhuizen hier relatief eenvoudig."
  },
  apeldoorn: {
    name: "Apeldoorn",
    province: "Gelderland",
    population: "165.000",
    avgPrice: "€400 - €1.100",
    neighborhoods: ["Centrum", "Zuid", "Osseveld", "De Maten", "Berg en Bos"],
    description: "Apeldoorn ligt aan de rand van de Veluwe met veel groen en ruime woningen. De stad is goed bereikbaar en biedt diverse woonmogelijkheden."
  },
  zwolle: {
    name: "Zwolle",
    province: "Overijssel",
    population: "130.000",
    avgPrice: "€400 - €1.000",
    neighborhoods: ["Binnenstad", "Stadshagen", "Zwolle-Zuid", "Assendorp", "Diezerpoort"],
    description: "Zwolle is een historische Hanzestad met een goed bewaard centrum. De mix van oude en nieuwe wijken vraagt om verhuizers die beide settings aankunnen."
  },
  amersfoort: {
    name: "Amersfoort",
    province: "Utrecht",
    population: "160.000",
    avgPrice: "€500 - €1.300",
    neighborhoods: ["Binnenstad", "Vathorst", "Hoogland", "Soesterkwartier", "Nieuwland"],
    description: "Amersfoort combineert een middeleeuwse binnenstad met moderne nieuwbouwwijken. De centrale ligging maakt het een populaire verhuisbestemming."
  },
  maastricht: {
    name: "Maastricht",
    province: "Limburg",
    population: "120.000",
    avgPrice: "€450 - €1.200",
    neighborhoods: ["Centrum", "Wyck", "Céramique", "Randwyck", "Limmel"],
    description: "Maastricht is de meest zuidelijke stad van Nederland met een Bourgondische sfeer. De historische binnenstad en heuvels vragen om ervaren verhuizers."
  },
  leiden: {
    name: "Leiden",
    province: "Zuid-Holland",
    population: "125.000",
    avgPrice: "€550 - €1.400",
    neighborhoods: ["Centrum", "Stevenshof", "Merenwijk", "De Kooi", "Mors"],
    description: "Leiden is een historische universiteitsstad met smalle grachten en monumentale panden. Verhuizen in het centrum vraagt vaak om een verhuislift."
  },
  dordrecht: {
    name: "Dordrecht",
    province: "Zuid-Holland",
    population: "120.000",
    avgPrice: "€400 - €1.000",
    neighborhoods: ["Centrum", "Sterrenburg", "Dubbeldam", "Stadspolders", "Wielwijk"],
    description: "Dordrecht is de oudste stad van Holland met een prachtig historisch centrum. De eilandligging biedt unieke verhuisuitdagingen."
  },
  zoetermeer: {
    name: "Zoetermeer",
    province: "Zuid-Holland",
    population: "125.000",
    avgPrice: "€400 - €1.000",
    neighborhoods: ["Centrum", "Oosterheem", "Rokkeveen", "Meerzicht", "Buytenwegh"],
    description: "Zoetermeer is een moderne stad met veel nieuwbouw en ruime straten. De goede bereikbaarheid maakt verhuizen hier vlot en efficiënt."
  },

  // 30 NEW cities for expanded local SEO coverage
  "den-bosch": {
    name: "'s-Hertogenbosch",
    province: "Noord-Brabant",
    population: "160.000",
    avgPrice: "€450 - €1.200",
    neighborhoods: ["Binnenstad", "Rosmalen", "Empel", "Engelen", "Maaspoort", "De Groote Wielen"],
    description: "'s-Hertogenbosch, ook wel Den Bosch genoemd, is een historische vestingstad met een prachtige middeleeuwse binnenstad. De smalle straatjes en monumentale panden vragen om ervaren verhuizers."
  },
  helmond: {
    name: "Helmond",
    province: "Noord-Brabant",
    population: "95.000",
    avgPrice: "€350 - €950",
    neighborhoods: ["Centrum", "Brandevoort", "Helmond-West", "Rijpelberg", "Mierlo-Hout"],
    description: "Helmond is een dynamische Brabantse stad met een mix van historische gebouwen en moderne architectuur. De goede bereikbaarheid maakt verhuizen hier efficiënt."
  },
  oss: {
    name: "Oss",
    province: "Noord-Brabant",
    population: "95.000",
    avgPrice: "€350 - €900",
    neighborhoods: ["Centrum", "Ruwaard", "Schadewijk", "Ussen", "Horzak"],
    description: "Oss is een gezellige Brabantse stad met betaalbare woningen en een sterke lokale gemeenschap. De compacte opzet maakt verhuizen hier praktisch."
  },
  roosendaal: {
    name: "Roosendaal",
    province: "Noord-Brabant",
    population: "77.000",
    avgPrice: "€350 - €900",
    neighborhoods: ["Centrum", "Tolberg", "Langdonk", "Kroeven", "Kalsdonk"],
    description: "Roosendaal ligt strategisch aan de Belgische grens met goede verbindingen. De stad biedt diverse woonmogelijkheden voor elke verhuisbehoefte."
  },
  "sittard-geleen": {
    name: "Sittard-Geleen",
    province: "Limburg",
    population: "92.000",
    avgPrice: "€350 - €900",
    neighborhoods: ["Sittard Centrum", "Geleen Centrum", "Limbrichterveld", "Sanderbout", "Kluis"],
    description: "Sittard-Geleen is een samengevoegde gemeente in Zuid-Limburg met diverse wijken. De heuvelachtige omgeving vraagt soms om extra verhuisaandacht."
  },
  venlo: {
    name: "Venlo",
    province: "Limburg",
    population: "102.000",
    avgPrice: "€350 - €950",
    neighborhoods: ["Centrum", "Blerick", "Tegelen", "Belfeld", "Arcen"],
    description: "Venlo ligt aan de Maas nabij de Duitse grens. De stad combineert historische charme met moderne logistiek en biedt diverse verhuismogelijkheden."
  },
  heerlen: {
    name: "Heerlen",
    province: "Limburg",
    population: "87.000",
    avgPrice: "€300 - €850",
    neighborhoods: ["Centrum", "Heerlerbaan", "Heerlen-Noord", "Hoensbroek", "Welten"],
    description: "Heerlen is de grootste stad van Parkstad Limburg met een rijke mijnbouwhistorie. De heuvelachtige straten vragen om verhuizers met lokale expertise."
  },
  roermond: {
    name: "Roermond",
    province: "Limburg",
    population: "60.000",
    avgPrice: "€350 - €900",
    neighborhoods: ["Centrum", "Donderberg", "Heide", "Maasniel", "Swalmen"],
    description: "Roermond is een historische Maasstad bekend om het Designer Outlet. De compacte binnenstad en waterrijke omgeving bieden unieke verhuisuitdagingen."
  },
  delft: {
    name: "Delft",
    province: "Zuid-Holland",
    population: "105.000",
    avgPrice: "€500 - €1.300",
    neighborhoods: ["Binnenstad", "Voorhof", "Buitenhof", "Tanthof", "TU-wijk"],
    description: "Delft is een historische universiteitsstad met iconische grachtenpanden en smalle straten. Verhuizen in het centrum vraagt om specialistische apparatuur."
  },
  deventer: {
    name: "Deventer",
    province: "Overijssel",
    population: "102.000",
    avgPrice: "€400 - €1.000",
    neighborhoods: ["Binnenstad", "Rivierenwijk", "Colmschate", "Keizerslanden", "Borgele"],
    description: "Deventer is een van de oudste steden van Nederland met een prachtige middeleeuwse binnenstad. De historische panden vragen om ervaren verhuizers."
  },
  leeuwarden: {
    name: "Leeuwarden",
    province: "Friesland",
    population: "125.000",
    avgPrice: "€350 - €950",
    neighborhoods: ["Centrum", "Huizum", "Camminghaburen", "Bilgaard", "Aldlân"],
    description: "Leeuwarden is de culturele hoofdstad van Friesland met een gezellige binnenstad. De Friese gastvrijheid maakt verhuizen hier een prettige ervaring."
  },
  alkmaar: {
    name: "Alkmaar",
    province: "Noord-Holland",
    population: "110.000",
    avgPrice: "€450 - €1.200",
    neighborhoods: ["Binnenstad", "Overdie", "Huiswaard", "De Mare", "Kooimeer"],
    description: "Alkmaar is beroemd om de kaasmarkt en heeft een charmante historische binnenstad. De grachtenpanden vragen vaak om een verhuislift."
  },
  hoofddorp: {
    name: "Hoofddorp",
    province: "Noord-Holland",
    population: "78.000",
    avgPrice: "€500 - €1.300",
    neighborhoods: ["Centrum", "Graan voor Visch", "Floriande", "Bornholm", "Pax"],
    description: "Hoofddorp ligt centraal nabij Schiphol met moderne infrastructuur. De relatief nieuwe bebouwing maakt verhuizen hier vaak eenvoudig."
  },
  purmerend: {
    name: "Purmerend",
    province: "Noord-Holland",
    population: "82.000",
    avgPrice: "€400 - €1.100",
    neighborhoods: ["Centrum", "Wheermolen", "Overwhere", "Purmer-Noord", "Weidevenne"],
    description: "Purmerend is een groeiende stad in de Zaanstreek met een mix van oud en nieuw. De goede verbindingen met Amsterdam maken het een populaire verhuisbestemming."
  },
  zaandam: {
    name: "Zaandam",
    province: "Noord-Holland",
    population: "78.000",
    avgPrice: "€400 - €1.100",
    neighborhoods: ["Centrum", "Zaandam-Zuid", "Kogerveldwijk", "Pelders- en Hoornseveld", "Rosmolenwijk"],
    description: "Zaandam is de hoofdstad van de Zaanstreek met een industrieel erfgoed. De karakteristieke houten huizen en moderne nieuwbouw bieden diverse verhuisopties."
  },
  hilversum: {
    name: "Hilversum",
    province: "Noord-Holland",
    population: "92.000",
    avgPrice: "€500 - €1.400",
    neighborhoods: ["Centrum", "Noord", "Oost", "Zuid", "Mediapark"],
    description: "Hilversum is de mediastad van Nederland met veel villa's en bosrijke omgeving. De gevarieerde woningvoorraad vraagt om flexibele verhuisoplossingen."
  },
  vlaardingen: {
    name: "Vlaardingen",
    province: "Zuid-Holland",
    population: "74.000",
    avgPrice: "€350 - €950",
    neighborhoods: ["Centrum", "Holy", "Westwijk", "Ambacht", "Vettenoord"],
    description: "Vlaardingen is een havenstad met een rijke visserijgeschiedenis. De compacte opzet en goede bereikbaarheid maken verhuizen hier efficiënt."
  },
  schiedam: {
    name: "Schiedam",
    province: "Zuid-Holland",
    population: "80.000",
    avgPrice: "€400 - €1.000",
    neighborhoods: ["Centrum", "Oost", "West", "Zuid", "Noord"],
    description: "Schiedam is beroemd om de jenever en historische molens. De monumentale binnenstad combineert met moderne wijken voor diverse verhuismogelijkheden."
  },
  gouda: {
    name: "Gouda",
    province: "Zuid-Holland",
    population: "75.000",
    avgPrice: "€400 - €1.100",
    neighborhoods: ["Binnenstad", "Goverwelle", "Bloemendaal", "Plaswijck", "Kort Haarlem"],
    description: "Gouda is wereldberoemd om kaas en stroopwafels. De historische binnenstad met grachtenpanden vraagt om zorgvuldig verhuizen."
  },
  "alphen-aan-den-rijn": {
    name: "Alphen aan den Rijn",
    province: "Zuid-Holland",
    population: "112.000",
    avgPrice: "€400 - €1.100",
    neighborhoods: ["Centrum", "Ridderveld", "De Baronie", "Kerk en Zanen", "Hazeveld"],
    description: "Alphen aan den Rijn ligt centraal in het Groene Hart. De waterrijke omgeving en diverse wijken bieden voor elk wat wils bij verhuizen."
  },
  spijkenisse: {
    name: "Spijkenisse",
    province: "Zuid-Holland",
    population: "72.000",
    avgPrice: "€350 - €900",
    neighborhoods: ["Centrum", "Waterland", "Groenewoud", "Schiekamp", "De Hoek"],
    description: "Spijkenisse is de grootste plaats op Voorne-Putten. De moderne opzet en goede metro-verbinding maken verhuizen hier praktisch."
  },
  "capelle-aan-den-ijssel": {
    name: "Capelle aan den IJssel",
    province: "Zuid-Holland",
    population: "68.000",
    avgPrice: "€400 - €1.000",
    neighborhoods: ["Centrum", "Oostgaarde", "Schollevaar", "Schenkel", "Fascinatio"],
    description: "Capelle aan den IJssel grenst aan Rotterdam met uitstekende verbindingen. De moderne wijken maken verhuizen hier relatief eenvoudig."
  },
  veenendaal: {
    name: "Veenendaal",
    province: "Utrecht",
    population: "68.000",
    avgPrice: "€400 - €1.000",
    neighborhoods: ["Centrum", "Dragonder", "Engelenburg", "De Vallei", "Franse Gat"],
    description: "Veenendaal ligt op de grens van Utrecht en Gelderland. De centrale ligging en moderne infrastructuur maken het een praktische verhuislocatie."
  },
  nieuwegein: {
    name: "Nieuwegein",
    province: "Utrecht",
    population: "65.000",
    avgPrice: "€450 - €1.100",
    neighborhoods: ["Stadscentrum", "Jutphaas", "Vreeswijk", "Batau", "Fokkesteeg"],
    description: "Nieuwegein is een jonge stad nabij Utrecht met moderne architectuur. De ruim opgezette wijken maken verhuizen hier doorgaans vlot."
  },
  zeist: {
    name: "Zeist",
    province: "Utrecht",
    population: "65.000",
    avgPrice: "€500 - €1.300",
    neighborhoods: ["Centrum", "Kerckebosch", "Nijenheim", "Couwenhoven", "Austerlitz"],
    description: "Zeist is een groene gemeente met veel landgoederen en bossen. De gevarieerde woningvoorraad van villa's tot appartementen vraagt om diverse verhuisexpertise."
  },
  ede: {
    name: "Ede",
    province: "Gelderland",
    population: "120.000",
    avgPrice: "€400 - €1.000",
    neighborhoods: ["Centrum", "Veldhuizen", "Kernhem", "Bennekom", "Lunteren"],
    description: "Ede is een groene gemeente aan de Veluwe met diverse dorpen. De bosrijke omgeving en gevarieerde bebouwing bieden unieke verhuissituaties."
  },
  harderwijk: {
    name: "Harderwijk",
    province: "Gelderland",
    population: "50.000",
    avgPrice: "€400 - €1.100",
    neighborhoods: ["Binnenstad", "Drielanden", "Stadsweiden", "Zeebuurt", "Hierden"],
    description: "Harderwijk is een gezellige Hanzestad aan het Wolderwijd. De historische binnenstad en waterkant bieden karakteristieke verhuislocaties."
  },
  doetinchem: {
    name: "Doetinchem",
    province: "Gelderland",
    population: "58.000",
    avgPrice: "€350 - €900",
    neighborhoods: ["Centrum", "De Huet", "Dichteren", "Overstegen", "Gaanderen"],
    description: "Doetinchem is het hart van de Achterhoek met een gezellig centrum. De Achterhoekse gastvrijheid maakt verhuizen hier een prettige ervaring."
  },
  hoogeveen: {
    name: "Hoogeveen",
    province: "Drenthe",
    population: "56.000",
    avgPrice: "€300 - €800",
    neighborhoods: ["Centrum", "Krakeel", "Wolfsbos", "De Weide", "Erflanden"],
    description: "Hoogeveen is een veenkoloniale stad in Drenthe met betaalbare woningen. De ruime opzet en goede bereikbaarheid maken verhuizen hier praktisch."
  },
  emmen: {
    name: "Emmen",
    province: "Drenthe",
    population: "108.000",
    avgPrice: "€300 - €850",
    neighborhoods: ["Centrum", "Angelslo", "Emmerhout", "Rietlanden", "Bargeres"],
    description: "Emmen is de grootste stad van Drenthe met diverse wijken en goede voorzieningen. De betaalbare woningmarkt maakt het een aantrekkelijke verhuisbestemming."
  },
  assen: {
    name: "Assen",
    province: "Drenthe",
    population: "70.000",
    avgPrice: "€300 - €850",
    neighborhoods: ["Centrum", "Marsdijk", "Peelo", "Pittelo", "Kloosterveen"],
    description: "Assen is de hoofdstad van Drenthe met een gezellig centrum en goede voorzieningen. De compacte stad met ruime straten maakt verhuizen hier praktisch."
  },
  meppel: {
    name: "Meppel",
    province: "Drenthe",
    population: "35.000",
    avgPrice: "€300 - €800",
    neighborhoods: ["Centrum", "Koedijkslanden", "Oosterboer", "Nieuwveense Landen"],
    description: "Meppel is een waterrijke stad in Drenthe bekend om de Meppeler Dag. De historische binnenstad en moderne wijken bieden diverse verhuismogelijkheden."
  },

  // Zeeland steden
  middelburg: {
    name: "Middelburg",
    province: "Zeeland",
    population: "48.000",
    avgPrice: "€350 - €950",
    neighborhoods: ["Binnenstad", "Dauwendaele", "Griffioen", "Stromenwijk", "Nieuw Middelburg"],
    description: "Middelburg is de hoofdstad van Zeeland met een prachtige historische binnenstad. De monumentale panden en smalle straatjes vragen om ervaren verhuizers."
  },
  vlissingen: {
    name: "Vlissingen",
    province: "Zeeland",
    population: "45.000",
    avgPrice: "€300 - €850",
    neighborhoods: ["Centrum", "Souburg", "Paauwenburg", "Westerzicht", "Rosenburg"],
    description: "Vlissingen is een havenstad met een rijke maritieme geschiedenis. De zeeboulevard en diverse wijken bieden gevarieerde verhuismogelijkheden."
  },
  goes: {
    name: "Goes",
    province: "Zeeland",
    population: "38.000",
    avgPrice: "€300 - €850",
    neighborhoods: ["Centrum", "Goes-Zuid", "Ouverture", "Aria", "Mannee"],
    description: "Goes is het centrum van Zuid-Beveland met uitstekende voorzieningen. De centrale ligging in Zeeland maakt het een populaire woonplaats."
  },
  terneuzen: {
    name: "Terneuzen",
    province: "Zeeland",
    population: "55.000",
    avgPrice: "€300 - €800",
    neighborhoods: ["Centrum", "Triniteit", "Othene", "Zuidpolder", "Driewegen-Westdorpe"],
    description: "Terneuzen is een havenstad in Zeeuws-Vlaanderen nabij België. De industriële haven en residentiële wijken bieden diverse verhuisopties."
  },

  // Extra Friese steden
  sneek: {
    name: "Sneek",
    province: "Friesland",
    population: "35.000",
    avgPrice: "€300 - €850",
    neighborhoods: ["Binnenstad", "Noorderhoek", "Zwette", "Domp", "Tinga"],
    description: "Sneek is de waterpoortstad van Friesland, beroemd om watersport. De historische binnenstad en waterrijke omgeving maken het een unieke verhuislocatie."
  },
  heerenveen: {
    name: "Heerenveen",
    province: "Friesland",
    population: "50.000",
    avgPrice: "€300 - €850",
    neighborhoods: ["Centrum", "De Greiden", "Skoatterwâld", "Midden", "Noord"],
    description: "Heerenveen is bekend van het Abe Lenstra Stadion en Thialf. De sportieve stad biedt moderne voorzieningen en betaalbare woningen."
  },
  drachten: {
    name: "Drachten",
    province: "Friesland",
    population: "45.000",
    avgPrice: "€300 - €850",
    neighborhoods: ["Centrum", "Noord", "Oost", "Zuid", "De Trisken"],
    description: "Drachten is de grootste plaats in de gemeente Smallingerland. De goed bereikbare stad met moderne wijken maakt verhuizen hier efficiënt."
  },
  amstelveen: {
    name: "Amstelveen",
    province: "Noord-Holland",
    population: "90.000",
    avgPrice: "€500 - €1.400",
    neighborhoods: ["Centrum", "Bovenkerk", "Westwijk", "Groenelaan", "Bankras-Kostverloren", "Waardhuizen", "Middenhoven", "Randwijck"],
    description: "Amstelveen grenst direct aan Amsterdam en biedt een groene, rustige woonomgeving met uitstekende voorzieningen. De internationale gemeenschap en nabijheid van Schiphol maken het populair bij expats en gezinnen."
  },
  rijswijk: {
    name: "Rijswijk",
    province: "Zuid-Holland",
    population: "55.000",
    avgPrice: "€400 - €1.100",
    neighborhoods: ["Centrum", "Bomenbuurt", "Muziekbuurt", "Plaspoelpolder", "Ypenburg", "Leeuwendaal", "Artiestenbuurt"],
    description: "Rijswijk ligt strategisch tussen Den Haag en Delft en biedt betaalbare woningen met een goede bereikbaarheid. Het stadscentrum is compact maar compleet met winkels en horeca."
  },
};

// Old slug → new slug mapping for redirects
export const citySlugRedirects: Record<string, string> = {
  denhaag: "den-haag",
  denbosch: "den-bosch",
  sittardgeleen: "sittard-geleen",
  alphenrijn: "alphen-aan-den-rijn",
  capelleijssel: "capelle-aan-den-ijssel",
};

// Get all city slugs for sitemap and navigation
export const getAllCitySlugs = (): string[] => {
  return Object.keys(cityData);
};

// Get cities grouped by province
export const getCitiesByProvince = (): Record<string, CityInfo[]> => {
  const grouped: Record<string, CityInfo[]> = {};
  
  Object.values(cityData).forEach(city => {
    if (!grouped[city.province]) {
      grouped[city.province] = [];
    }
    grouped[city.province].push(city);
  });
  
  return grouped;
};
